import { useState, type FormEvent } from "react";
import { api } from "../api/client";
import { useErrorHandler } from "../auth/AuthContext";
import { formatCents, parseDollarsToCents } from "../lib/money";
import { ErrorMessage, Field, Modal } from "./common";
import { TransferDone } from "./TransferDone";

type Bucket = "spend" | "save";

/** What just moved, kept so the confirmation can animate it. */
interface Moved {
  amountCents: number;
  from: Bucket;
  fromBeforeCents: number;
  toBeforeCents: number;
  /** The completed transfer's ledger ID, which replays the animation if a
   *  child moves money twice without closing the modal. */
  id: string;
}

/**
 * Moves money between the child's own buckets.
 *
 * The direction is picked first, as two big labelled cards rather than a
 * dropdown — a kid should see "Save → Spend" as a picture of what will happen,
 * not read it out of a menu. The amount is always typed positive; the sign is
 * the server's business, so a child never meets a minus sign.
 *
 * On success this swaps into TransferDone, mirroring how SpendMoney swaps into
 * SpendConfirm. Until then the only feedback a move gave was the modal
 * closing, which is indistinguishable from having done nothing at all.
 */
export function TransferMoney({
  childId,
  spendCents,
  saveCents,
  onClose,
  onTransferred,
}: {
  childId: string;
  spendCents: number;
  saveCents: number;
  onClose: () => void;
  onTransferred: () => Promise<void> | void;
}) {
  const toMessage = useErrorHandler();

  const [from, setFrom] = useState<Bucket>("save");
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [moved, setMoved] = useState<Moved | null>(null);

  const to: Bucket = from === "save" ? "spend" : "save";
  const available = from === "save" ? saveCents : spendCents;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError("");

    const cents = parseDollarsToCents(amount);
    if (cents === null || cents <= 0) {
      setError("Enter how much you want to move, like 2.50");
      return;
    }
    // Checked here for a friendly message; the server enforces it too, and its
    // answer is the one that counts.
    if (cents > available) {
      setError(
        `That's more than your ${label(from)} money. You have ${formatCents(available)} there.`,
      );
      return;
    }

    setBusy(true);
    try {
      const legs = await api.transfer(childId, {
        amountCents: cents,
        from,
        to,
      });
      // The before-figures are captured here rather than read back after the
      // refresh: the animation's job is to show the change, so it needs the
      // side of it that no longer exists on the server.
      setMoved({
        amountCents: cents,
        from,
        fromBeforeCents: available,
        toBeforeCents: from === "save" ? spendCents : saveCents,
        id: legs.to.id,
      });
      await onTransferred();
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (moved) {
    return (
      <TransferDone
        amountCents={moved.amountCents}
        from={moved.from}
        fromBeforeCents={moved.fromBeforeCents}
        toBeforeCents={moved.toBeforeCents}
        replayKey={moved.id}
        onClose={onClose}
      />
    );
  }

  return (
    <Modal title="Move your money" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <ErrorMessage>{error}</ErrorMessage>

        <Field label="Which way?">
          <div className="transfer-dirs" role="group">
            {(["save", "spend"] as Bucket[]).map((source) => {
              const target = source === "save" ? "spend" : "save";
              const selected = from === source;
              return (
                <button
                  key={source}
                  type="button"
                  className={`transfer-dir ${selected ? "transfer-dir--on" : ""}`}
                  aria-pressed={selected}
                  onClick={() => {
                    setFrom(source);
                    setError("");
                  }}
                >
                  <span className="transfer-dir__flow">
                    <span className={`transfer-dir__chip transfer-dir__chip--${source}`}>
                      {label(source)}
                    </span>
                    <span className="transfer-dir__arrow" aria-hidden="true">
                      →
                    </span>
                    <span className={`transfer-dir__chip transfer-dir__chip--${target}`}>
                      {label(target)}
                    </span>
                  </span>
                  <span className="transfer-dir__hint">
                    {source === "save"
                      ? "Use savings to buy something now"
                      : "Put spending money away"}
                  </span>
                </button>
              );
            })}
          </div>
        </Field>

        <Field
          label="How much?"
          hint={`You have ${formatCents(available)} in ${label(from)}.`}
        >
          <input
            className="input"
            inputMode="decimal"
            autoFocus
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>

        <p className="muted">
          Moving money doesn't change how much you have in total — it just
          changes which bucket it's in.
        </p>

        <div className="row row--between">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? "Moving…" : "Move it"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function label(bucket: Bucket): string {
  return bucket === "save" ? "Save" : "Spend";
}
